import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { parse, stringify } from 'query-string';

import { FormattedMessage } from '../../../util/reactIntl';
import { reportListingApi, checkListingReportStatusApi } from '../../../util/api';

import { Modal, PrimaryButton } from '../../../components';
import ReportListingForm from './ReportListingForm';

import css from './ReportListing.module.css';

// No flag icon exists yet under src/assets/icons or src/components (checked) - a small
// inline one here rather than adding a new asset file for a single one-off use.
const FlagIcon = props => (
  <svg
    className={props.className}
    width="18"
    height="18"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 1.5V14.5M3 2H12.2L10.4 4.5L12.2 7H3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Self-contained "Report listing" feature: the trigger button, the modal, the form, and all
 * of its own submit/loading/error/duplicate-check state. Deliberately not wired into Redux -
 * this is UI-local state nothing else in the app needs, so plain hooks (matching e.g.
 * UserCard.js's own useState usage) keep this a two-line drop-in for either ListingPage
 * layout variant rather than needing new action creators/reducer wiring in
 * ListingPage.duck.js.
 *
 * @param {Object} props
 * @param {propTypes.listing} props.listing - the listing being viewed
 * @param {propTypes.currentUser} [props.currentUser] - the logged-in user, if any
 * @param {Function} props.onManageDisableScrolling - same prop threaded to every other
 *   Modal on ListingPage, so this one participates in the same body-scroll-lock bookkeeping
 */
const ReportListing = props => {
  const { listing, currentUser, onManageDisableScrolling } = props;

  const history = useHistory();
  const location = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);
  const [statusCheckDone, setStatusCheckDone] = useState(false);

  const listingId = listing?.id?.uuid;
  const isAuthenticated = !!currentUser?.id;

  // Check once (per mount, i.e. once per listing-page visit) whether this user has already
  // reported this listing - not from listing data itself (metadata is Integration-API-only,
  // never returned to the client via the public SDK), but via the dedicated status endpoint,
  // which only ever answers with a boolean, never the underlying report content.
  useEffect(() => {
    let isMounted = true;

    if (isAuthenticated && listingId) {
      checkListingReportStatusApi({ listingId })
        .then(result => {
          if (isMounted) {
            setAlreadyReported(!!result?.alreadyReported);
            setStatusCheckDone(true);
          }
        })
        .catch(() => {
          // Non-fatal: worst case the button stays enabled and the real (server-side)
          // duplicate check on submit catches it instead.
          if (isMounted) {
            setStatusCheckDone(true);
          }
        });
    } else {
      setStatusCheckDone(true);
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, listingId]);

  const handleReportClick = () => {
    if (!isAuthenticated) {
      // Same "open the login modal on top of this page, come straight back" pattern as the
      // Contact button (see ListingPage.shared.js's handleContactUser) - just without that
      // one's extra bookkeeping to auto-reopen ITS modal afterwards, since re-clicking
      // Report once logged back in is a perfectly reasonable ask here.
      const currentUrl = `${location.pathname}${location.search || ''}${location.hash || ''}`;
      const { pathname, search, state } = location;
      const paramsObj = parse(search);
      paramsObj.login = 'open';
      paramsObj.redirect = encodeURIComponent(currentUrl);
      history.push(`${pathname}?${stringify(paramsObj)}`, state);
      return;
    }

    setSubmitError(null);
    setSubmitSuccess(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSubmitError(null);
  };

  const handleSubmit = values => {
    setSubmitInProgress(true);
    setSubmitError(null);

    const { reason, otherReasonText, description } = values;

    reportListingApi({
      listingId,
      reason,
      otherReasonText: reason === 'other' ? otherReasonText : undefined,
      description,
    })
      .then(() => {
        setSubmitInProgress(false);
        setSubmitSuccess(true);
        setAlreadyReported(true);
      })
      .catch(e => {
        setSubmitInProgress(false);

        if (e?.code === 'already_reported') {
          // A race - e.g. two tabs, or the initial status check happened before an earlier
          // submission from this same user finished - the server is the real gate either way.
          setAlreadyReported(true);
        }

        setSubmitError(
          e?.error || e?.message || 'Something went wrong submitting your report. Please try again.'
        );
      });
  };

  if (!listingId) {
    return null;
  }

  // One report per user per listing (client decision 2026-09-03). Once alreadyReported is
  // true - from the mount-time status check, or immediately after a successful submit - the
  // button becomes a disabled, permanent "Bereits gemeldet" and stays that way after the
  // modal closes (this is the same isAuthenticated-scoped state driving the label below, not
  // reset anywhere on close).
  const isDisabled = isAuthenticated && (alreadyReported || !statusCheckDone);

  const handleKeyDown = e => {
    // A plain div (not a <button>) doesn't get Enter/Space activation or focus styling for
    // free - added by hand so this stays keyboard-accessible without pulling in any of the
    // shared Button component's own composed CSS (border/padding from Button.module.css/
    // marketplaceDefaults.css) - kept clashing with the "no button chrome, just icon+text"
    // look the client asked for, no matter which Button variant/override was tried.
    if (isDisabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleReportClick();
    }
  };

  return (
    <div className={css.root}>
      <div
        role="button"
        tabIndex={0}
        className={css.reportButton}
        onClick={isDisabled ? undefined : handleReportClick}
        onKeyDown={handleKeyDown}
        aria-disabled={isDisabled}
        data-disabled={isDisabled || undefined}
      >
        <FlagIcon className={css.flagIcon} />
        <FormattedMessage
          id={alreadyReported ? 'ReportListing.alreadyReportedButton' : 'ReportListing.reportButton'}
        />
      </div>

      <Modal
        id="ListingPage.reportListing"
        contentClassName={css.modalContent}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {submitSuccess ? (
          <div className={css.successContent}>
            <h2 className={css.successHeading}>
              <FormattedMessage id="ReportListing.successHeading" />
            </h2>
            <p className={css.successText}>
              <FormattedMessage id="ReportListing.successText" />
            </p>
            <PrimaryButton type="button" onClick={handleCloseModal}>
              <FormattedMessage id="ReportListing.successCloseButton" />
            </PrimaryButton>
          </div>
        ) : (
          <>
            <h2 className={css.modalHeading}>
              <FormattedMessage id="ReportListing.modalHeading" />
            </h2>
            <ReportListingForm
              onSubmit={handleSubmit}
              onCancel={handleCloseModal}
              inProgress={submitInProgress}
              serverError={submitError}
              alreadyReported={alreadyReported}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ReportListing;
